import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { quoteTypeSchema, QuoteTypeFormData } from '../schemas/master-data.schemas';
import { QuoteType } from '../types/master-data.types';
import { useModelStore } from '@/features/pricing-models/store/model-store';
import { Loader2 } from 'lucide-react';

interface QuoteTypeFormProps {
  quoteType?: QuoteType;
  quoteTypes?: QuoteType[];
  onSubmit: (data: QuoteTypeFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function QuoteTypeForm({
  quoteType,
  quoteTypes = [],
  onSubmit,
  onCancel,
  isLoading,
}: QuoteTypeFormProps) {
  const { models, fetchModels } = useModelStore();
  const [selectedModelId, setSelectedModelId] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<QuoteTypeFormData>({
    resolver: zodResolver(quoteTypeSchema),
    defaultValues: quoteType
      ? {
          code: quoteType.code,
          name: quoteType.name,
          description: quoteType.description || '',
          order: quoteType.order,
          codePrefix: quoteType.codePrefix,
          upstreamTypeId: quoteType.upstreamTypeId || '',
          upstreamVariableCode: quoteType.upstreamVariableCode || '',
          sapCode: quoteType.sapCode ?? undefined,
        }
      : {
          code: '',
          name: '',
          description: '',
          order: 0,
          codePrefix: '',
          upstreamTypeId: '',
          upstreamVariableCode: '',
          sapCode: undefined,
        },
  });

  const watchUpstreamTypeId = watch('upstreamTypeId');
  const watchUpstreamVariableCode = watch('upstreamVariableCode');

  // Cargar modelos al montar
  useEffect(() => {
    fetchModels();
  }, []);

  // Si estamos editando y ya tiene variable, intentar detectar el modelo
  useEffect(() => {
    if (quoteType?.upstreamVariableCode && models.length > 0 && !selectedModelId) {
      const matchingModel = models.find((m) =>
        m.variables.some((v) => v.code === quoteType.upstreamVariableCode)
      );
      if (matchingModel) {
        setSelectedModelId(matchingModel.id);
      }
    }
  }, [quoteType, models, selectedModelId]);

  // Limpiar variable cuando se quita el upstream
  useEffect(() => {
    if (!watchUpstreamTypeId) {
      setValue('upstreamVariableCode', '');
      setSelectedModelId('');
    }
  }, [watchUpstreamTypeId, setValue]);

  // Limpiar variable cuando se cambia el modelo
  const handleModelChange = (modelId: string) => {
    setSelectedModelId(modelId);
    // Solo limpiar la variable si el modelo cambió y la variable actual no existe en el nuevo modelo
    if (modelId) {
      const model = models.find((m) => m.id === modelId);
      const varExists = model?.variables.some((v) => v.code === watchUpstreamVariableCode);
      if (!varExists) {
        setValue('upstreamVariableCode', '');
      }
    } else {
      setValue('upstreamVariableCode', '');
    }
  };

  // Filter out current quote type from upstream options to prevent self-reference
  const upstreamOptions = quoteTypes.filter((qt) => qt.id !== quoteType?.id);

  // Variables del modelo seleccionado (solo las de input, no calculadas)
  const selectedModel = models.find((m) => m.id === selectedModelId);
  const availableVariables = selectedModel?.variables.filter((v) => !v.formula) || [];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Código *</Label>
          <Input
            id="code"
            placeholder="IMPORT"
            {...register('code')}
            error={errors.code?.message}
            disabled={isLoading || !!quoteType}
            className="uppercase"
          />
          <p className="text-xs text-muted-foreground">Código único del tipo de cotización</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            placeholder="Importación"
            {...register('name')}
            error={errors.name?.message}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="codePrefix">Prefijo de Código *</Label>
          <Input
            id="codePrefix"
            placeholder="IMP"
            {...register('codePrefix')}
            error={errors.codePrefix?.message}
            disabled={isLoading}
            className="uppercase"
          />
          <p className="text-xs text-muted-foreground">
            Prefijo para generar códigos de cotización (ej: IMP-001)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="order">Orden</Label>
          <Input
            id="order"
            type="number"
            min={0}
            placeholder="0"
            {...register('order')}
            error={errors.order?.message}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">Orden de visualización en la cadena</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="upstreamTypeId">Tipo Upstream</Label>
          <SearchableSelect
            value={watchUpstreamTypeId || ''}
            onValueChange={(val) => setValue('upstreamTypeId', val)}
            disabled={isLoading}
            options={[
              { value: '', label: 'Sin tipo upstream' },
              ...upstreamOptions.map((qt) => ({ value: qt.id, label: `${qt.name} (${qt.code})` })),
            ]}
            placeholder="Sin tipo upstream"
          />
          <p className="text-xs text-muted-foreground">
            Tipo que alimenta precios a este tipo (cadena de precios)
          </p>
        </div>

        {/* Modelo - solo se muestra si hay upstream seleccionado */}
        {watchUpstreamTypeId && (
          <div className="space-y-2">
            <Label htmlFor="modelSelect">Modelo de Pricing</Label>
            <SearchableSelect
              value={selectedModelId}
              onValueChange={(val) => handleModelChange(val)}
              disabled={isLoading}
              options={[
                { value: '', label: 'Seleccionar modelo...' },
                ...models
                  .filter((m) => m.isActive)
                  .map((m) => ({ value: m.id, label: `${m.name}${m.code ? ` (${m.code})` : ''}` })),
              ]}
              placeholder="Seleccionar modelo..."
            />
            <p className="text-xs text-muted-foreground">
              Modelo del cual seleccionar la variable donde se inyectará el precio upstream
            </p>
          </div>
        )}

        {/* Variable - solo se muestra si hay modelo seleccionado */}
        {watchUpstreamTypeId && selectedModelId && (
          <div className="space-y-2">
            <Label htmlFor="upstreamVariableCode">Variable Upstream</Label>
            <SearchableSelect
              value={watchUpstreamVariableCode || ''}
              onValueChange={(val) => setValue('upstreamVariableCode', val)}
              disabled={isLoading}
              options={[
                { value: '', label: 'Seleccionar variable...' },
                ...availableVariables.map((v) => ({
                  value: v.code,
                  label: `${v.name} (${v.code})`,
                })),
              ]}
              placeholder="Seleccionar variable..."
            />
            <p className="text-xs text-muted-foreground">
              Variable del modelo donde se inyectará el precio aprobado del tipo upstream
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          placeholder="Descripción del tipo de cotización..."
          {...register('description')}
          disabled={isLoading}
          rows={3}
        />
        {errors.description?.message && (
          <p className="text-sm text-destructive">{errors.description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="sapCode">Código SAP</Label>
        <Input
          id="sapCode"
          placeholder="Código equivalente en SAP (opcional)"
          maxLength={64}
          {...register('sapCode')}
          error={(errors as any).sapCode?.message}
          disabled={isLoading}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : quoteType ? (
            'Actualizar'
          ) : (
            'Crear'
          )}
        </Button>
      </div>
    </form>
  );
}

export default QuoteTypeForm;
